import { Label } from "@/components/atoms/texts/label";
import ControlsDownload from "@/components/molecules/controlsDownload";
import getCurrencySymbolFromCode from "@/utils/getCurrencySymbolFromCode";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetFooter, SheetSlot } from "@/components/templates/sheet";
import FormEditBills from "@/components/macro-organisms/panels/panels-admin/Bills/FormEditBills";
import { useEffect, useRef, useState } from "react";
import { useAddInvoice } from "@/services/admin-services/mutations/useAddConsultantInvoice";
import { useUpdateInvoice } from "@/services/admin-services/mutations/useUpdateConsultantInvoice";
import LogoBoondManager from '@/assets/logos/logoboondmanager.svg';
import { useDeleteConsultantInvoice } from "@/services/admin-services/mutations/useDeleteConsultantInvoice";
import { useTranslation } from "react-i18next";
import getMonthName from "@/utils/getMonthName";
import { useToast } from "@/components/organisms/feedback/use-toast";

export default function Invoice({ data, deleteConsultantInvoice, dataOwner, verticalView, hideControls = false }) {
    const [dataForDisplay, setDataForDisplay] = useState(data);
    const symbol = getCurrencySymbolFromCode(dataForDisplay?.currency);
    const submitRef = useRef(null);
    const [openEditBills, setOpenEditBills] = useState(false);
    const { updateInvoice, loading: loadingUpdate, error: errorUpdate, data: dataUpdate } = useUpdateInvoice();
    const [t] = useTranslation("global");
    const { toast } = useToast();

    const [editData, setEditData] = useState(null);
    const [submissionError, setSubmissionError] = useState(null);
    const [submissionSuccess, setSubmissionSuccess] = useState(null);

    useEffect(() => {
        dataUpdate && setDataForDisplay(dataUpdate.updateConsultantInvoice);
        setOpenEditBills(false);
        // setSubmissionSuccess(true);

        dataUpdate && toast({
            title: t("success.backendOperation.update"),
            description: t("consultants.statement.invoices.invoice") + " " + (dataForDisplay?.clientName) + " : " + (dataForDisplay?.month) + " - " + dataForDisplay?.year,
            variant: "success",
            duration: 5000,
            isClosable: true,
        });
    }, [dataUpdate]);

    useEffect(() => {
        setSubmissionError(errorUpdate);
    }, [errorUpdate]);

    const handleSubmit = async (formData) => {
        try {
            setSubmissionError(null);
            setSubmissionSuccess(false);
            // console.log("commission to send ", formData.manualCommission, parseFloat(formData?.commission) !== 0, formData.manualCommission || parseFloat(formData?.commission) !== 0)
            const updatedInvoice = {
                uuid: editData?.uuid,
                invoiceDate: formData.invoiceDate,
                periodEnd: formData.periodEnd,
                periodStart: formData.periodStart,
                paymentDate: formData.paymentDate, // ajout du champ paymentDate
                invoiceNumber: formData.billNumber,
                clientName: formData.customer,
                daysWorked: parseFloat(formData.days),
                dailyRateConsultant: parseFloat(formData.tJMConsultant),
                dailyRateClient: parseFloat(formData.tJMClient),
                currency: formData.currency,
                commission: parseFloat(formData.commission),
                manualCommission: formData?.manualCommission == undefined ? parseFloat(formData?.commission) !== 0 : formData?.manualCommission,
            };

            console.log("handleSubmit, updatedInvoice =", updatedInvoice);
            if (editData) {
                await updateInvoice({
                    variables: { updatedInvoice },
                });
            }
        } catch (err) {
            setSubmissionError(err.message);
            console.error("Error during submission:", err);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteConsultantInvoice(dataForDisplay?.uuid);
            setDataForDisplay(null); // Marquer comme supprimé
            setSubmissionSuccess(true);
            setOpenEditBills(false);
        } catch (err) {
            console.error("Error during deletion:", err);
        }
    };

    // Si la facture est supprimée, afficher un message ou un espace vide
    if (!dataForDisplay) {
        return (
            <div className="bg-white w-full shadow-md px-paddingLeftAndRight py-paddingTopAndBottom rounded-sm justify-start items-center inline-flex gap-secondDegree">
                <Label fontWeight="bold" className="text-gray-500">
                    {t("consultants.statement.invoices.deleted")}
                </Label>
            </div>
        );
    }
    function formatDateFR(dateStr) {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR");
    }
    const handleClose = (close) => {
        setSubmissionError(null);
        setSubmissionSuccess(null);
        setOpenEditBills(close);
    };

    useEffect(() => {
        if (!openEditBills && submissionSuccess) {
            setSubmissionSuccess(null);
        }
    }, [openEditBills, submissionSuccess]);

    return (
        <div className={`bg-white w-full shadow-md px-paddingLeftAndRight py-paddingTopAndBottom rounded-sm justify-start items-center inline-flex gap-secondDegree` + (verticalView ? ` flex-col ` : '')}>
            <div className={"flex flex-col gap-firstDegree"}>
                <Label fontWeight="bold">{dataForDisplay?.clientName} </Label>

                <div className="flex gap-betweenText flex-wrap ">
                    <Label message="consultants.statement.invoices.missionOf" />
                    <Label fontWeight="semibold">{dataForDisplay?.daysWorked} </Label>
                    <Label message={data.daysWorked > 1 ? "consultants.statement.invoices.daysPlural" : "consultants.statement.invoices.daysSingular"} />
                    <Label message="consultants.statement.invoices.from" />
                    <Label fontWeight="semibold">{formatDateFR(dataForDisplay?.periodStart)} </Label>
                    <Label message="consultants.statement.invoices.to" />
                    <Label fontWeight="semibold">{formatDateFR(dataForDisplay?.periodEnd)} </Label>
                    <Label message="consultants.statement.invoices.forPeriod" />
                    <Label fontWeight="semibold">{dataForDisplay?.month}/{dataForDisplay?.year} </Label>
                </div>
                {dataOwner && <div className="flex gap-betweenText items-center flex-wrap">
                    <Label message="consultants.statement.invoices.invoiceNumber" />
                    <Label fontWeight="semibold">{dataForDisplay?.invoiceNumber} </Label>
                    <Label fontWeight="" message="consultants.statement.invoices.invoiceDate" />
                    <Label fontWeight="semibold">{formatDateFR(dataForDisplay?.invoiceDate)} </Label>
                    <Label message={dataForDisplay?.paymentDate ? "consultants.statement.invoices.paidOn" : "consultants.statement.invoices.notPaid"} />
                    <Label fontWeight="semibold">{formatDateFR(dataForDisplay?.paymentDate)}</Label>
                    {data?.boondManagerInvoiceId && window.ENV.BOOND_MANAGER_ACTIVE_MODE && <a href={window.ENV.BOOND_MANAGER_URL + "invoices/" + data?.boondManagerInvoiceId + "/information"} target="blank" className="ml-2"><img src={LogoBoondManager} className="h-[24px]"></img></a>}
                </div>}
            </div>
            <div className={!verticalView && `grow`}></div>
            <div className={` flex gap-firstDegree flex-col min-w-[100px] ${verticalView && ` w-full`}`}>
                {dataOwner && <div className="flex gap-firstDegree flex-wrap">
                    <Label fontWeight="semibold" message="consultants.statement.invoices.amountClient" />
                    <Label>{dataForDisplay?.amountClient.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                </div>}
                {dataOwner ? <div className="flex gap-firstDegree flex-wrap">
                    <Label fontWeight="semibold" message="consultants.statement.invoices.amountConsultant" />
                    <Label>{dataForDisplay?.amountConsultant.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                </div> :
                    <div className="flex gap-firstDegree flex-wrap">
                        <Label fontWeight="semibold" message="consultants.statement.invoices.amount" />
                        <Label>{dataForDisplay?.amountConsultant.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                    </div>
                }
            </div>
            <div className={` flex gap-firstDegree flex-col  min-w-[100px]` + (verticalView && ` w-full`)}>
                {dataOwner && <div className="flex gap-firstDegree flex-wrap">
                    <Label fontWeight="semibold" message="consultants.statement.invoices.dailyRateClient" />
                    <Label>{dataForDisplay?.dailyRateClient.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                </div>}
                {dataOwner ? <div className="flex gap-firstDegree flex-wrap">
                    <Label fontWeight="semibold" message="consultants.statement.invoices.dailyRateConsultant" />
                    <Label>{dataForDisplay?.dailyRateConsultant.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                </div> :
                    <div className="flex gap-firstDegree flex-wrap">
                        <Label fontWeight="semibold" message="consultants.statement.invoices.dailyRate" />
                        <Label>{dataForDisplay?.dailyRateConsultant.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>
                    </div>}
                {/* {(dataForDisplay?.manualCommission == true) && <div className="flex gap-firstDegree flex-wrap">
                    <Label fontWeight="semibold" message="consultants.statement.invoices.commission" />
                    <Label>{dataForDisplay?.commission.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</Label>

                </div> }  */}
                <div className="flex gap-firstDegree flex-wrap group">
                    {dataOwner ? <Label fontWeight="semibold" message="consultants.statement.invoices.commissionConsultant" />
                        : <Label fontWeight="semibold" message="consultants.statement.invoices.commission" />}
                    {(dataForDisplay?.manualCommission === false) ?
                        <div className="flex gap-firstDegree flex-wrap text-disabled">
                            <span className="flex group-hover:hidden">
                                <Label message="">
                                    {dataForDisplay?.commission.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}
                                </Label>
                            </span>
                            <span className="hidden group-hover:flex">
                                <Label message="consultants.statement.invoices.auto" />
                            </span>
                        </div>
                        : <div className="flex gap-firstDegree flex-wrap">
                            <Label message="" className="">
                                {dataForDisplay?.commission?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}
                            </Label>
                        </div>
                    }
                    <div className="flex gap-firstDegree flex-wrap">
                        <Label fontWeight="semibold" message="consultants.statement.billsDetails.commission" />
                        <Label message={`consultants.statement.billsDetails.${dataForDisplay?.manualCommission ? "manual" : "auto"}`} />
                    </div>
                    {/* <Label className={` ${dataForDisplay?.manualCommission == false && `text-disabled group`} `}>
                        <p className="">{dataForDisplay?.commission.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{symbol}</p>
                        <p className="hidden group-hover:flex ">Auto</p>

                    </Label> */}

                </div>

            </div>
            {/* TODO : traduire */}
            <ControlsDownload
                dataOwner={true}
                hideOpenNewTab
                hideDownload
                hideEdit={hideControls || !dataOwner}
                hideDelete={hideControls || !dataOwner}
                onClickEdit={() => { setOpenEditBills(true); setEditData(data) }}
                titleForDelete={t("consultants.statement.invoices.invoice") + " : " + (dataForDisplay?.month && getMonthName(dataForDisplay?.month)) + " - " + dataForDisplay?.year}
                onClickDelete={handleDelete}
                hFull={!verticalView}
            />
            <Sheet open={openEditBills} onOpenChange={handleClose}>
                <SheetContent>
                    <SheetHeader
                        title={editData ? "consultants.statement.billsDetails.updateBill" : "consultants.statement.overflow.seeBillsDetails"}
                        subheader={"global.information.allFieldsMandatory"}
                    />
                    <SheetSlot className="flex relative">
                        <FormEditBills
                            onSubmit={handleSubmit}
                            submitRef={submitRef}
                            global={false}
                            submissionSuccess={submissionSuccess}
                            submissionError={submissionError}
                            editData={dataForDisplay}
                        />
                    </SheetSlot>
                    <SheetFooter
                        buttonOutlineText={"global.actions.back"}
                        buttonDressedText={"global.actions.save"}
                        onClickDressed={() => submitRef.current?.click()}
                        onClickOutline={() => handleClose(false)}
                    />
                </SheetContent>
            </Sheet>
        </div>
    );
}